import { Injectable } from '@angular/core';
import { RadioOption } from '@carm/shared/components/form-elements/data/radio-options';
import * as MetadataActions from '@carm/_configs/store/actions/metadata.actions';
import * as MetadataStore from '@carm/_configs/store/reducers/metadata.reducer';
import * as MetadataSelectors from '@carm/_configs/store/selectors/metadata.selectors';
import { OperationMetadataTypes } from '@carm/_core/business-registration/models';
import { EBondStatus } from '@carm/_core/financial-security/models/financial-security.model';
import { IKeyValue } from '@carm/_utils/models';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as dayjs from 'dayjs';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CadMetadataStatusKeys, KeyValue } from '../models/metadata.model';

@Injectable()
export class MetadataService {
  public r1CutoverDate: Date;
  public r2CutoverDate: Date;
  public isMetadataLoaded = false;

  constructor(
    protected store: Store<MetadataStore.MetadataState>,
    private translateService: TranslateService
  ) {
  }

  /**
   * Loads metadata as per Language Specified by default 'en'
   * @param [language] i.e 'en' or 'fr'
   */
  loadMetadata(language: string = 'en') {
    this.store.dispatch(MetadataActions.loadAllMetadata({ lang: language }));
    this.isMetadataLoaded = true;
  }


  resetMetadata() {
    this.store.dispatch(MetadataActions.removeAllMetadata());
    this.isMetadataLoaded = false;
  }

  /**
   * Gets country list
   * @returns KeyValue pairs array
   */
  getCountryList(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCountryList),
      map(countries => {
        const canadaAndUsCodes = ['CA', 'US'];
        const onlyCanadaAndUs = countries?.filter(country => canadaAndUsCodes.includes(country.key)).slice().sortByValue() || [];
        const allExceptCanadaAndUs = countries?.slice().sortByValue().filter(country => !canadaAndUsCodes.includes(country.key)) || [];
        return [
          ...onlyCanadaAndUs,
          ...allExceptCanadaAndUs
        ];
      }));
  }

  getJurisdictionCountryList(): Observable<KeyValue[]> {
    return this.getCountryList().pipe(
      map(countries => countries?.filter(country => country.key !== 'CA'))
    );
  }

  /**
   * Gets regions list
   * @returns KeyValue pairs array
   */
  getRegionsList(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getRegionsList));
  }

  getRegionsByParentKey(parentKey: string): Observable<KeyValue[]> {
    return this.getRegionsList().pipe(
      map((data: KeyValue[]) =>
        data
          .slice()
          .filter(item => item.parentKey === parentKey)
          .sort((a, b) => a.value.localeCompare(b.value))
      )
    );
  }

  getPortOfEntryRegions(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getPortOfEntryRegions));
  }

  getPortsOfEntryByRegion(regionKey: string): Observable<KeyValue[]> {
    return this.getEntryPorts().pipe(
      map((data: KeyValue[]) =>
        data
          .slice()
          .filter(item => item.parentKey === regionKey)
          .sortByKey()
          .prefixDisplayWithKey()
      )
    );
  }

  getCanadianProvinces(): Observable<KeyValue[]> {
    return this.getRegionsByParentKey('CA');
  }

  getUsStates(): Observable<KeyValue[]> {
    return this.getRegionsByParentKey('US');
  }

  /**
   * Gets transaction questions for Link Business Page
   * @returns KeyValue pairs array
   */
  getTransactionQuestions(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getQuestions));
  }

  /**
   * Gets security questions for Personal Profile Page
   * @returns KeyValue pairs array
   */
  getSecurityQuestions(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSecurityQuestion));
  }

  getResources(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getResources));
  }

  getAdvanceRulingTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getAdvanceRulingTypes));
  }

  getRulingTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getRulingTypes));
  }

  getRulingEntryPorts(): Observable<KeyValue[]> {
    return this.getEntryPorts().pipe(map((x) => x
              .filter((k) => k.key !== 'ports_of_entry')
              .map((kv) => {
                return { key: kv.key, value: `${kv.key.trim().padStart(3, '0')} - ${kv.value}` } as KeyValue;
              })
              .sort((value1, value2) => value1.value < value2.value ? -1 : 1)));
  }

  getEntryPorts(): Observable<KeyValue[]> {
    // "ports of entry" returns key + parentKey instead of key + value.
    // Backend should be changing so it's like the others but until then, there are no "value".
    return this.store.pipe(select(MetadataSelectors.getEntryPorts));
  }

  getDocumentProgramCategories(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCaseDocumentProgramCategories));
  }

  /**
   * Gets Category Types
   * @returns KeyValue pairs array
   */
  getDocumentCategories(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCaseDocumentCategories));
  }

    /**
   * Gets Document Category Types L1
   * @returns KeyValue pairs array
   */
  getCategoriesL1(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCaseDocumentCategoriesL1));
  }

    /**
   * Gets Document Category Types L2
   * @returns KeyValue pairs array
   */
  // getCategoriesL2(): Observable<KeyValue[]> {
  //   return this.store.pipe(
  //     select(MetadataSelectors.getCaseDocumentCategoriesL2)
  //   );
  // }

  /**
   * Gets Business Structures Types
   * @returns KeyValue pairs array
   */
  getBusinessStructures(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessStructures));
  }

  getBusinessStructuresValidateBusiness(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessStructuresS2));
  }

  getNotificationTypes(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getNotificationTypes),
      map(list => list?.slice().sortByValue())
    );
  }

  getNotificationCategories(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getNotificationCategories),
      map(list => list?.slice().sortByValue())
    );
  }

  getImporterSubProgramTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramImporterTypes));
  }

  getCarrierSubProgramTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramCarrierTypes));
  }

  getProgramSubTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramSubTypes));
  }

  getThirdPartyServiceTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramThirdPartyTypes));
  }

  getThirdPartyProviderCategories(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getEdiServiceProviderCategories));
  }

  getThirdPartyConnectionMethods(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getEdiConnectionMethods));
  }

  getThirdPartyEdiServiceTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getEdiServiceTypes));
  }

  getWarehouseSubProgramTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramWarehouseTypes));
  }

  getEnrolmentFileTemplateCodes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramEnrolmentFileTemplates));
  }

  getRegistrationProgramTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessRegistrationPrograms));
  }

  getAddressTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getAddressTypes));
  }

  getSocialMediaTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSocialMediaTypes));
  }

  getProgramAddressTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramAddresses));
  }

  getBusinessAddressTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessAddresses));
  }

  getProgramEnrolmentTypes(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getProgramChoices),
      map(list => list?.slice().sortByValue())
    );
  }

  getSubProgramEnrolmentTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSubProgramChoices));
  }

  /**
   * Gets Operations Types, depends on business structure
   * @returns KeyValue pairs array
   */
  getOperationTypesByBusinessStructure(
    businessStructure: string
  ): Observable<KeyValue[]> {

    switch (businessStructure) {
      case OperationMetadataTypes.Corporation:
        return this.store.pipe(
          select(MetadataSelectors.getOperationCorporationTypes)
        );
      case OperationMetadataTypes.Individual:
        return this.store.pipe(
          select(MetadataSelectors.getOperationIndividualTypes)
        );
      case OperationMetadataTypes.Partnership:
        return this.store.pipe(
          select(MetadataSelectors.getOperationPartnershipTypes)
        );
    }
  }

  getSoaTradeReasons(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSoaTradeReasons));
  }

  getSoaSimaReasons(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSoaSimaReasons));
  }

  getJurisdictions(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getJurisdictions));
  }

  getTelephoneTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getDeviceLocations) );
  }

  getDeviceTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getDevicesTypes));
  }

  getCountryPhoneCodes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getInternationalTelephoneCodes));
  }

  getLanguages(): Observable<KeyValue[]> {
    return this.store
      .pipe(select(MetadataSelectors.getLanguages))
      .pipe(map(list => list?.slice().sortByValue()));
  }

  /**
   * Gets contact titles
   * @returns KeyValue pairs array
   */
  getContactTitles(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getContactS4Titles));
  }

  getContactTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramContactTypes));
  }

  getThirdPartyRelationShipStatus(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getThirdPartyBusinessRelationStatuses));
  }

  getThirdPartyRelationShipAccessType(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getThirdPartyBusinessAccessTypes)
    );
  }

  getStatusesByCadType(
    cadStatusKey: CadMetadataStatusKeys
  ): Observable<IKeyValue[]> {

    if(!cadStatusKey){
      return of([]);
    }

    switch (cadStatusKey) {
      case CadMetadataStatusKeys.Declaration:
        return this.store.pipe(
          select(MetadataSelectors.getDeclarationStatuses)
        );
      case CadMetadataStatusKeys.DDB:
        return this.store.pipe(
          select(MetadataSelectors.getDutyDrawbackStatuses)
        );
      case CadMetadataStatusKeys.MassAdjustment:
        return this.store.pipe(
          select(MetadataSelectors.getMassAdjustmentStatuses)
        );
      case CadMetadataStatusKeys.ROI:
        return this.store.pipe(
          select(MetadataSelectors.getRoiStatuses)
        );
      case CadMetadataStatusKeys.TempImport:
        return this.store.pipe(
          select(MetadataSelectors.getTempImportStatuses)
        );
      default:
        throw Error('unknown metadata status');
    }
  }

  getStatusLocalized(
    cadStatusKey: CadMetadataStatusKeys,
    statusCode: string
  ): Observable<string> {
    return this.getStatusesByCadType(cadStatusKey).pipe(
      map(s => s.find(kv => kv.key === statusCode)?.value)
    );
  }

  getCurrencies(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCurrencies));
  }

  getClearingStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getClearingStatuses));
  }

  getYesNoOptions(): RadioOption[] {
    return [
      { text: this.translateService.instant('form.yes'), value: true },
      { text: this.translateService.instant('form.no'), value: false },
    ];
  }

  billingCycleOptions(): RadioOption[] {
    return [
      {
        text: this.translateService.instant(
          'program_account.profile.details.sub_programs.trusted_trader.billing_cycle.option_1'
        ),
        value: this.translateService.instant(
          'program_account.profile.details.sub_programs.trusted_trader.billing_cycle.option_1'
        ),
      },
      {
        text: this.translateService.instant(
          'program_account.profile.details.sub_programs.trusted_trader.billing_cycle.option_2'
        ),
        value: this.translateService.instant(
          'program_account.profile.details.sub_programs.trusted_trader.billing_cycle.option_2'
        ),
      },
    ];
  }

  getConfiguration(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getConfiguration));
  }

  getBusinessPreferredCorrespondenceTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessPreferredCorrespondenceTypes));
  }

  getBusinessOffsettingPreferenceTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBusinessOffsettingPreferenceTypes));
  }

  getLegistativeAuthority(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getLegislativeAuthorities));
  }

  getProgramTypesToLegislativeAuthority(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramTypesToLegislativeAuthority));
  }

  getEdiProgramType(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getEdiProgramTypes));
  }

  getEdiConnectionMethod(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getEnrolmentEdiConnectionMethods));
  }

  getBondProgramReasonCode(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBondProgramReasonCodes)
    );
  }

  getPenaltyCategories(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getPenaltyCategories)
    );
  }

  getAppealTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getAppealTypes));
  }

  getPenaltyAppealTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getPenaltyAppealTypes));
  }

  getSeizureForfeituresAppealTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getSeizureForfeituresAppealTypes));
  }

  getRppClaimsStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getRppClaimsStatuses));
  }

  getServiceRquestReasons(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getServiceRquestReasons));
  }

  getProgramComplianceStatusTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramComplianceStatusTypes));
  }

  getProgramComplianceSubProgramTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramComplianceSubProgramTypes));
  }

  getProgramComplianceReasons(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getProgramComplianceReasons));
  }

  getRppDecisionTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getRppDecisionTypes));
  }

  getPostedBondStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getFinancialSecurityBondClaimsStatuses),
      map(statuses => statuses
        .filter(s => s.key !== EBondStatus.UNDER_GUARANTOR_APPROVAL)
        .filter(s => s.key !== EBondStatus.ERROR)
        .filter(s => s.key !== EBondStatus.PARTIALLY_RELEASED)
      )
    )
  }

  getPendingApprovalBondStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(
      select(MetadataSelectors.getFinancialSecurityBondClaimsStatuses),
      map(statuses => statuses.filter(s => s.key === EBondStatus.UNDER_GUARANTOR_APPROVAL))
    )
  }

  getBondClaimStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getBondClaimStatuses));
  }

  getCorrectionTypes(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCorrectionTypes));
  }

  getCADReportStatuses(): Observable<KeyValue[]> {
    return this.store.pipe(select(MetadataSelectors.getCADReportStatuses));
  }

  getPaymentStatuses(): Observable<IKeyValue[]> {
    return of([
      { key: "NP", value: this.translateService.instant('enforcement.enforcement-actions.grid.not_paid') },
      { key: "X", value: this.translateService.instant('enforcement.enforcement-actions.grid.fully_paid') },
    ]);
  }

  getCutoverDate(): Observable<string> {
    // R1 go-live date
    return this.store.pipe(
      select(MetadataSelectors.getConfiguration),
      map(keyValues => {
        return keyValues?.find(keyValue => keyValue.key === 'cutoverdate')?.value
      })
    );
  }

  getR2CutoverDate(): Observable<string> {
    return this.store.pipe(
      select(MetadataSelectors.getConfiguration),
      map(keyValues => {
        // R2 go-live date
        return keyValues?.find(keyValue => keyValue.key === 'r2cutoverdate')?.value
      })
    );
  }

  public setCutoverDates() {
    combineLatest([
      this.getCutoverDate(),
      this.getR2CutoverDate()
    ])
    .subscribe(([cutoverdate, r2CutoverDate]) => {
      this.r1CutoverDate = dayjs(cutoverdate).toDate();
      this.r2CutoverDate = dayjs(r2CutoverDate).toDate();
    });
  }

  public getMonerisMaxAmountAllowed(): Observable<string> {
    return this.store.pipe(
      select(MetadataSelectors.getConfiguration),
      map(keyValues => {
        return keyValues?.find(keyValue => keyValue.key === 'monerisCCMaxAmount')?.value
      })
    );
  }
}
